import { useState } from "react";
import { Check, Copy, RotateCcw, Sparkles, X } from "lucide-react";

interface JsonSchemaEditorProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

type ValidationState = "empty" | "valid" | "invalid";

export default function JsonSchemaEditor({
  label,
  name,
  value,
  onChange,
  placeholder,
  rows = 8,
}: JsonSchemaEditorProps) {
  const [copied, setCopied] = useState(false);

  const getValidationState = (): { validState: ValidationState; errorMsg: string } => {
    if (!value.trim()) {
      return { validState: "empty", errorMsg: "" };
    }
    try {
      JSON.parse(value);
      return { validState: "valid", errorMsg: "" };
    } catch (e) {
      return { validState: "invalid", errorMsg: (e as Error).message };
    }
  };

  const { validState, errorMsg } = getValidationState();

  const handleFormat = () => {
    try {
      onChange(JSON.stringify(JSON.parse(value), null, 2));
    } catch {
      // not valid JSON, nothing to format
    }
  };

  const handleCopy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const borderClass =
    validState === "valid"
      ? "border-green-400/50 focus-within:border-green-500"
      : validState === "invalid"
        ? "border-red-400/50 focus-within:border-red-500"
        : "border-black/10 focus-within:border-accent";

  return (
    <div>
      {/* Label + toolbar */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <label className="text-sm font-semibold text-foreground/60 uppercase tracking-widest">
          {label}
        </label>

        <div className="flex items-center gap-1">
          {validState === "valid" && (
            <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-500/10 border border-green-500/20 px-2 py-0.5 rounded-full mr-1">
              <Check className="size-2.5" />
              Válido
            </span>
          )}
          {validState === "invalid" && (
            <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full mr-1">
              <X className="size-2.5" />
              Inválido
            </span>
          )}

          <button
            type="button"
            onClick={handleFormat}
            disabled={validState !== "valid"}
            title="Formatear JSON"
            className="flex items-center gap-1 text-[11px] font-semibold text-foreground/50 hover:text-accent hover:bg-accent/5 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1 rounded-lg transition-all"
          >
            <Sparkles className="size-3" />
            Formatear
          </button>

          <button
            type="button"
            onClick={handleCopy}
            disabled={!value}
            title="Copiar al portapapeles"
            className="flex items-center gap-1 text-[11px] font-semibold text-foreground/50 hover:text-accent hover:bg-accent/5 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1 rounded-lg transition-all"
          >
            {copied ? (
              <Check className="size-3 text-green-500" />
            ) : (
              <Copy className="size-3" />
            )}
            {copied ? "Copiado" : "Copiar"}
          </button>

          <button
            type="button"
            onClick={() => onChange("")}
            disabled={!value}
            title="Limpiar"
            className="flex items-center gap-1 text-[11px] font-semibold text-foreground/50 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1 rounded-lg transition-all"
          >
            <RotateCcw className="size-3" />
            Limpiar
          </button>
        </div>
      </div>

      {/* Editor area */}
      <div
        className={`rounded-xl border transition-colors duration-200 overflow-hidden ${borderClass}`}
      >
        {/* Top bar */}
        <div className="flex items-center gap-1.5 px-3 py-2 bg-black/3 border-b border-black/5">
          <span className="ml-2 text-[10px] font-medium text-foreground/30 uppercase tracking-wider">
            JSON
          </span>
        </div>

        <textarea
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          spellCheck={false}
          autoComplete="off"
          className="w-full px-4 py-3 bg-white/50 text-sm text-foreground font-mono leading-relaxed focus:outline-none resize-y placeholder:text-foreground/20"
        />
      </div>

      {/* Inline error */}
      {validState === "invalid" && errorMsg && (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-red-500 font-medium">
          <X className="size-3.5 mt-0.5 shrink-0" />
          {errorMsg}
        </p>
      )}
    </div>
  );
}
